import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getInitials, formatHandicap, formatDate } from '../utils/format';
import Icon from '../components/Icon';
import { useToast } from '../context/ToastContext';
import './Connections.css';

const LEVEL_OPTIONS = [
  { value: 'acquaintance',    label: 'Acquaintance',     badge: 'badge-gray'   },
  { value: 'playingPartner',  label: 'Playing Partner',  badge: 'badge-green'  },
  { value: 'preferredPartner',label: 'Preferred Partner',badge: 'badge-orange' },
];

function getLevelInfo(level) {
  return LEVEL_OPTIONS.find(o => o.value === level) || LEVEL_OPTIONS[0];
}

function Connections() {
  const navigate = useNavigate();
  const toast = useToast();
  const [connections, setConnections]  = useState([]);
  const [pending, setPending]          = useState([]);
  const [sent, setSent]                = useState([]);
  const [activeTab, setActiveTab]      = useState('connections');
  const [loading, setLoading]          = useState(true);
  const [levelUpdating, setLevelUpdating] = useState(null); // connectionId being updated
  const [actionPending, setActionPending] = useState(null); // connectionId with accept/decline/remove in flight

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [connectionsData, pendingData, sentData] = await Promise.all([
        api.getConnections(),
        api.getPendingConnections(),
        api.getSentConnections(),
      ]);
      setConnections(connectionsData);
      setPending(pendingData);
      setSent(sentData);
    } catch (err) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (connectionId) => {
    setActionPending(connectionId);
    try {
      await api.acceptConnection(connectionId);
      await loadData();
    } catch (err) { toast.error('Failed to accept connection'); }
    finally { setActionPending(null); }
  };

  const handleDecline = async (connectionId) => {
    setActionPending(connectionId);
    try {
      await api.declineConnection(connectionId);
      setPending(prev => prev.filter(p => p.id !== connectionId));
    } catch (err) { toast.error('Failed to decline connection'); }
    finally { setActionPending(null); }
  };

  const handleRemove = async (connectionId) => {
    if (!window.confirm('Remove this connection?')) return;
    setActionPending(connectionId);
    try {
      await api.removeConnection(connectionId);
      setConnections(prev => prev.filter(c => c.id !== connectionId));
    } catch (err) { toast.error('Failed to remove connection'); }
    finally { setActionPending(null); }
  };

  const handleMessage = async (userId) => {
    try {
      const conv = await api.getOrCreateDirectConversation(userId);
      navigate(`/messages/${conv.id}`);
    } catch (err) { toast.error('Failed to start conversation'); }
  };

  const handleLevelChange = async (connectionId, newLevel) => {
    setLevelUpdating(connectionId);
    try {
      await api.updateConnectionLevel(connectionId, newLevel);
      setConnections(prev =>
        prev.map(c => c.id === connectionId ? { ...c, connection_level: newLevel } : c)
      );
    } catch (err) { toast.error('Failed to update connection level'); }
    finally { setLevelUpdating(null); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div className="connections-page container">
      <div className="connections-header">
        <h1>Connections</h1>
        <p>The people you've played with</p>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'connections' ? 'active' : ''}`} onClick={() => setActiveTab('connections')}>
          Connections ({connections.length})
        </button>
        <button className={`tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending {pending.length > 0 && <span className="tab-badge">{pending.length}</span>}
        </button>
        <button className={`tab ${activeTab === 'sent' ? 'active' : ''}`} onClick={() => setActiveTab('sent')}>
          Sent ({sent.length})
        </button>
      </div>

      {/* ── Accepted connections ── */}
      {activeTab === 'connections' && (
        <div className="connections-list">
          {connections.length > 0 ? connections.map(c => {
            const levelInfo = getLevelInfo(c.connection_level);
            return (
              <div key={c.id} className="connection-card card">
                <Link to={`/golfer/${c.other_user_id}`} className="connection-info">
                  <div className="avatar">
                    {c.other_user_photo
                      ? <img src={c.other_user_photo} alt={c.other_user_name} />
                      : getInitials(c.other_user_name)}
                  </div>
                  <div className="connection-details">
                    <h3>{c.other_user_name}</h3>
                    <p className="connection-meta">
                      {formatHandicap(c.other_user_handicap) && <span>{formatHandicap(c.other_user_handicap)} hdcp</span>}
                      {c.other_user_city && <span> · {c.other_user_city}{c.other_user_state ? `, ${c.other_user_state}` : ''}</span>}
                    </p>
                    {c.met_at_course ? (
                      <p className="connection-met-at">
                        <Icon name="flag" size={13} /> Met at {c.met_at_course}
                        {c.created_at && <span className="connection-met-date"> · {formatDate(c.created_at)}</span>}
                      </p>
                    ) : c.created_at ? (
                      <p className="connection-met-at connection-met-date">Connected {formatDate(c.created_at)}</p>
                    ) : null}
                  </div>
                </Link>

                {/* Inline level selector */}
                <div className="connection-level-row">
                  <select
                    className={`level-select badge ${levelInfo.badge}`}
                    value={c.connection_level || 'acquaintance'}
                    disabled={levelUpdating === c.id}
                    onChange={e => handleLevelChange(c.id, e.target.value)}
                  >
                    {LEVEL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div className="connection-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => handleMessage(c.other_user_id)}>
                    Message
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={actionPending === c.id} onClick={() => handleRemove(c.id)}>
                    Remove
                  </button>
                </div>
              </div>
            );
          }) : (
            <div className="empty-state card">
              <div className="empty-state-icon"><Icon name="people" size={24} /></div>
              <p className="empty-state-title">No connections yet</p>
              <p className="empty-state-text">Find golfers near you and send a connection request.</p>
              <Link to="/search" className="btn btn-primary" style={{ marginTop: 16 }}>Find Golfers</Link>
            </div>
          )}
        </div>
      )}

      {/* ── Pending (incoming) ── */}
      {activeTab === 'pending' && (
        <div className="connections-list">
          {pending.length > 0 ? pending.map(req => (
            <div key={req.id} className="connection-card card">
              <Link to={`/golfer/${req.sender_id}`} className="connection-info">
                <div className="avatar">
                  {req.sender_photo
                    ? <img src={req.sender_photo} alt={req.sender_name} />
                    : getInitials(req.sender_name)}
                </div>
                <div className="connection-details">
                  <h3>{req.sender_name}</h3>
                  <p className="connection-meta">
                    {formatHandicap(req.sender_handicap) && <span>{formatHandicap(req.sender_handicap)} hdcp</span>}
                    {req.sender_home_course && <span> · {req.sender_home_course}</span>}
                  </p>
                  {req.sender_city && (
                    <p className="connection-meta">{req.sender_city}{req.sender_state ? `, ${req.sender_state}` : ''}</p>
                  )}
                  {req.met_at_course && (
                    <p className="connection-met-at"><Icon name="flag" size={13} /> Met at {req.met_at_course}</p>
                  )}
                  {req.note && (
                    <p className="connection-note">"{req.note}"</p>
                  )}
                </div>
              </Link>
              <div className="connection-actions">
                <button className="btn btn-primary btn-sm" disabled={actionPending === req.id} onClick={() => handleAccept(req.id)}>Accept</button>
                <button className="btn btn-secondary btn-sm" disabled={actionPending === req.id} onClick={() => handleDecline(req.id)}>Decline</button>
              </div>
            </div>
          )) : (
            <div className="empty-state card">
              <div className="empty-state-icon"><Icon name="inbox" size={24} /></div>
              <p className="empty-state-title">No pending requests</p>
              <p className="empty-state-text">When someone sends you a connection request, it will appear here. In the meantime, go find golfers to connect with!</p>
              <Link to="/search" className="btn btn-primary" style={{ marginTop: 16 }}>Find Golfers</Link>
            </div>
          )}
        </div>
      )}

      {/* ── Sent (outgoing) ── */}
      {activeTab === 'sent' && (
        <div className="connections-list">
          {sent.length > 0 ? sent.map(req => (
            <div key={req.id} className="connection-card card">
              <Link to={`/golfer/${req.recipient_id}`} className="connection-info">
                <div className="avatar">
                  {req.recipient_photo
                    ? <img src={req.recipient_photo} alt={req.recipient_name} />
                    : getInitials(req.recipient_name)}
                </div>
                <div className="connection-details">
                  <h3>{req.recipient_name}</h3>
                  <p className="connection-meta">
                    {formatHandicap(req.recipient_handicap) && <span>{formatHandicap(req.recipient_handicap)} hdcp</span>}
                    {req.recipient_home_course && <span> · {req.recipient_home_course}</span>}
                  </p>
                  <p className="connection-met-at connection-met-date sent-tag">
                    <Icon name="clock" size={13} /> Awaiting response · {formatDate(req.created_at)}
                  </p>
                </div>
              </Link>
            </div>
          )) : (
            <div className="empty-state card">
              <div className="empty-state-icon"><Icon name="send" size={24} /></div>
              <p className="empty-state-title">No sent requests</p>
              <p className="empty-state-text">You haven't sent any connection requests yet. Search for golfers near you and start building your network!</p>
              <Link to="/search" className="btn btn-primary" style={{ marginTop: 16 }}>Find Golfers</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Connections;
